import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { Eye, EyeOff, ArrowRight, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { COINIS_LOGO_DARK } from '../../../config';
import { authService } from '../../../services/auth';
import { generateErrorMessages } from '../../../utils/showApiErrors';
import { PasswordStrengthIndicator } from '../../setup/components/PasswordStrengthIndicator';
import { usePasswordValidation } from '../../setup/hooks/usePasswordValidation';

// Zod validation schema
const passwordExpiredSchema = z.object({
  oldPassword: z.string().min(1, 'Please enter your old password'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters long')
});

type PasswordExpiredFormValues = z.infer<typeof passwordExpiredSchema>;

export function PasswordExpired({ onLoginSuccess }: { onLoginSuccess: () => void }): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);

  const form = useForm<PasswordExpiredFormValues>({
    resolver: zodResolver(passwordExpiredSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: ''
    },
    mode: 'onTouched'
  });

  const newPasswordValue = useWatch({ name: 'newPassword', control: form.control });
  const { passwordStrength, showPasswordStrength } = usePasswordValidation(newPasswordValue);

  const onSuccess = () => {
    onLoginSuccess();
  };

  const handleSubmit = async (values: PasswordExpiredFormValues) => {
    try {
      setIsLoading(true);
      await authService.changeExpiredPassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      });
      onSuccess();
    } catch (e) {
      setError(generateErrorMessages(e as AxiosError));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <img src={COINIS_LOGO_DARK} className="h-7 w-auto" alt="Coinis" />

      {/* Header */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-gold-50">
          <AlertTriangle className="h-8 w-8 text-brand-gold" />
        </div>
        <h1 className="mb-2 font-onboarding-1 text-[1.625rem] font-bold leading-[1.1] tracking-[-0.02em] text-primary">
          Password Expired
        </h1>
        <p className="text-[15px] leading-[1.5] text-[var(--ink-2)]">
          Your password has expired. Please enter a new password to continue.
        </p>
      </div>

      {/* Security Alert */}
      <Alert className="border-brand-gold/30 bg-brand-gold-50">
        <AlertTriangle className="h-4 w-4 text-brand-gold" />
        <AlertDescription className="text-brand-orange">
          For security reasons, you must update your password to continue accessing your account.
        </AlertDescription>
      </Alert>

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="space-y-6"
      >
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--ink)]">Current Password</label>
          <div className="relative">
            <Input
              type={showOldPassword ? 'text' : 'password'}
              placeholder="Enter your current password"
              className="pr-11"
              aria-invalid={!!form.formState.errors.oldPassword}
              {...form.register('oldPassword')}
            />
            <button
              type="button"
              onClick={() => setShowOldPassword(!showOldPassword)}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)] transition hover:text-[var(--ink)]"
            >
              {showOldPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {form.formState.errors.oldPassword && (
            <p className="mt-1.5 text-xs text-loss">{form.formState.errors.oldPassword.message}</p>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--ink)]">New Password</label>
          <div className="relative">
            <Input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter your new password"
              className="pr-11"
              aria-invalid={!!form.formState.errors.newPassword}
              {...form.register('newPassword')}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)] transition hover:text-[var(--ink)]"
            >
              {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {form.formState.errors.newPassword && (
            <p className="mt-1.5 text-xs text-loss">{form.formState.errors.newPassword.message}</p>
          )}
        </div>

        <PasswordStrengthIndicator strength={passwordStrength} showStrength={showPasswordStrength} />

        {error && (
          <div className="rounded-lg border border-loss/20 bg-loss/5 px-4 py-3 text-sm text-loss">
            {Array.isArray(error) ? (
              <div className="space-y-1">
                {error.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            ) : (
              error
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Updating Password...</span>
            </div>
          ) : (
            <>
              Update Password
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
